import { inject, Injectable } from '@angular/core';
import type { AbstractControl } from '@angular/forms';

import { Actions } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import type { Observable } from 'rxjs';
import { EMPTY, map, of, switchMap, throwError } from 'rxjs';

import type {
  AuthenticationFormGroup,
  AuthModuleTranslations,
  User,
} from '@core/components-authorization-legacy';
import { AuthenticationDialogService } from '@core/components-authorization-legacy';
// eslint-disable-next-line boundaries/element-types
import { TellentSecureChangeService } from '@core/tellent-data'; // FIXME
import { TellentCurrentUserDataFacade } from '@core/tellent-logic';
import { resolveMemberInitials } from '@core/tellent-util';
import type { NotificationRef } from '@core/user-interface-legacy';

export const tellentAuthModuleTranslations: AuthModuleTranslations = {
  authenticationForm: {
    emailVerificationCode: 'tlc.current_user.authentication_form.email_verification_code',
    usePassword: 'tlc.current_user.authentication_form.use_password',
    resendCode: 'tlc.current_user.authentication_form.resend_code',
    currentPassword: 'tlc.current_user.authentication_form.current_password',
    placeholder: 'tlc.current_user.authentication_form.placeholder',
    useVerificationCode: 'tlc.current_user.authentication_form.use_verification_code',
    authentication: 'tlc.current_user.authentication_form.authentication',
    toContinueVerify: 'tlc.current_user.authentication_form.to_continue_verify',
    ssoEnabledInfobox: 'tlc.current_user.authentication_form.sso_enabled_infobox',
  },
  adminPasswordForm: {
    changePassword: 'tlc.current_user.admin_password_form.change_password',
    newPassword: 'tlc.current_user.admin_password_form.new_password',
    yourPassword: 'tlc.current_user.admin_password_form.your_password',
    currentPassword: 'tlc.current_user.admin_password_form.current_password',
    currentPasswordPlaceholder: 'tlc.current_user.admin_password_form.current_password_placeholder',
    oneNumber: 'tlc.current_user.admin_password_form.one_number',
    passwordLength: 'tlc.current_user.admin_password_form.password_length',
    useLowerUpper: 'tlc.current_user.admin_password_form.use_lower_upper',
    confirmNewPassword: 'tlc.current_user.admin_password_form.confirm_new_password',
    title: 'tlc.current_user.admin_password_form.title',
  },
  common: {
    or: 'tlc.common.or',
    cancel: 'tlc.common.cancel',
    continue: 'tlc.common.continue',
    goBack: 'tlc.common.go_back',
  },
  dialog_2fa_app: {
    step_1: {
      header: 'tlc.current_user.dialog_2fa_app.step_1.header',
      header_step: 'tlc.current_user.dialog_2fa_app.step_1.header_step',
      header_description: 'tlc.current_user.dialog_2fa_app.step_1.header_description',
      description: 'tlc.current_user.dialog_2fa_app.step_1.description',
      picture_alt: 'tlc.current_user.dialog_2fa_app.step_1.picture_alt',
    },
    step_2: {
      header: 'tlc.current_user.dialog_2fa_app.step_2.header',
      header_step: 'tlc.current_user.dialog_2fa_app.step_2.header_step',
      header_description: 'tlc.current_user.dialog_2fa_app.step_2.header_description',
      infobox: 'tlc.current_user.dialog_2fa_app.step_2.infobox',
      code_header: 'tlc.current_user.dialog_2fa_app.step_2.code_header',
      manual_code: 'tlc.current_user.dialog_2fa_app.step_2.manual_code',
      cta: 'tlc.current_user.dialog_2fa_app.step_2.cta',
    },
  },
  dialog_2fa_sms: {
    number_required: 'tlc.current_user.dialog_2fa_sms.number_required',
    number_length: 'tlc.current_user.dialog_2fa_sms.number_length',
    number_invalid: 'tlc.current_user.dialog_2fa_sms.number_invalid',
    success_toast: {
      title: 'tlc.current_user.dialog_2fa_sms.success_toast.title',
      message: 'tlc.current_user.dialog_2fa_sms.success_toast.message',
    },
    step_1: {
      header: 'tlc.current_user.dialog_2fa_sms.step_1.header',
      header_step: 'tlc.current_user.dialog_2fa_sms.step_1.header_step',
      header_description: 'tlc.current_user.dialog_2fa_sms.step_1.header_description',
      phone: 'tlc.current_user.dialog_2fa_sms.step_1.phone',
      phone_placeholder: 'tlc.current_user.dialog_2fa_sms.step_1.phone_placeholder',
      phone_description: 'tlc.current_user.dialog_2fa_sms.step_1.phone_description',
      number_is_invalid: 'tlc.current_user.dialog_2fa_sms.step_1.number_is_invalid',
      infobox_title: 'tlc.current_user.dialog_2fa_sms.step_1.infobox_title',
      infobox_description: 'tlc.current_user.dialog_2fa_sms.step_1.infobox_description',
      security_link: 'tlc.help_articles.t2fa_sms_step_1',
    },
    step_2: {
      header: 'tlc.current_user.dialog_2fa_sms.step_2.header',
      header_step: 'tlc.current_user.dialog_2fa_sms.step_2.header_step',
      header_description: 'tlc.current_user.dialog_2fa_sms.step_2.header_description',
      resend: 'tlc.current_user.dialog_2fa_sms.step_2.resend',
      code_sent: 'tlc.current_user.dialog_2fa_sms.step_2.code_sent',
      activation_code: 'tlc.current_user.dialog_2fa_sms.step_2.activation_code',
      warning_title: 'tlc.current_user.dialog_2fa_sms.step_2.warning_title',
      warning_description: 'tlc.current_user.dialog_2fa_sms.step_2.warning_description',
      security_link: 'tlc.help_articles.t2fa_sms_step_2',
      set_up_now: 'tlc.current_user.dialog_2fa_sms.step_2.set_up_now',
    },
  },
};
@Injectable({ providedIn: 'root' })
export class TellentAdminAuthenticationDialogService extends AuthenticationDialogService {
  private readonly currentUserFacade = inject(TellentCurrentUserDataFacade);
  public readonly errorMessages: Record<
    keyof AuthenticationFormGroup['controls'],
    Record<string, string>
  > = {
    password: {
      invalid_password: '',
    },
    emailCode: {
      invalid_email_code: '',
    },
  };

  public store = inject(Store);
  public actions$ = inject(Actions);

  public user: User = {
    email: '',
    firstName: '',
    hasAvatar: false,
    initials: '',
    lastName: '',
    photoThumbUrl: '',
  };

  public codeService = inject(TellentSecureChangeService);

  public currentUser$ = this.currentUserFacade.currentUser$;

  public canChangeVerificationType = false;

  constructor() {
    super();
    this.init();
  }

  public requestEmailCode(): any {
    this.codeService.sendCode();
    return EMPTY;
  }
  public createEmailCodeProgressNotification(): NotificationRef {
    // @ts-ignore
    return undefined;
  }

  public submit(form: AuthenticationFormGroup, skipCompany: boolean): Observable<string> {
    return this.validateCode(form).pipe(map(() => form.value.emailCode!));
  }

  private init(): void {
    this.currentUser$.subscribe(({ email, firstName, lastName }) => {
      this.user = {
        ...this.user,
        email,
        firstName,
        lastName,
        initials: resolveMemberInitials({ firstName, lastName, email }),
      };
    });
  }

  private validateCode(control: AbstractControl<{ emailCode?: string }>) {
    return this.codeService.validateCode(control.value.emailCode!).pipe(
      switchMap(payload => {
        if (!payload.ok) {
          this.errorMessages.emailCode.invalid_email_code = payload.error;
          return throwError(() => ({ emailCode: payload.error }));
        }

        this.errorMessages.emailCode.invalid_email_code = '';
        return of({ emailCode: null });
      }),
    );
  }
}
